import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Hexagon, Calendar, Users, Clock, CheckSquare, Settings,
  BarChart3, LogOut, Menu, X, Plus, CalendarDays, XCircle, Building2,
  ChevronLeft, ChevronRight, Palmtree, Landmark, Cake, Home, Heart,
  Briefcase, Plane, Baby, GraduationCap, Stethoscope, Coffee, Umbrella, Sun, Thermometer,
  CalendarCheck, FileText, CircleCheck, CircleDashed, Ban, Loader2, KeyRound
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import Sidebar from '../components/Sidebar';
import RequestLeaveDialog from '../components/RequestLeaveDialog';
import QuickBookPopover from '../components/QuickBookPopover';
import AllowanceExplainer from '../components/AllowanceExplainer';
import NextLeaveYear from '../components/NextLeaveYear';
import CarryOverLine from '../components/CarryOverLine';
import { currentLeaveYear, leaveYearHeading, leaveYearLabel, calendarYearsOf, inLeaveYear,
  leaveYearSpan, leaveYearMonths, leaveYearMonthIndex, leaveYearInitials, leaveYearRange } from '../lib/leaveYear';
import YourPendingRequests from '../components/YourPendingRequests';
import { useAuth } from '../context/AuthContext';
// ⚠️ Pure, and CHECKED — `node src/lib/workingDays.checks.mjs`. Replaces date-fns
// `isWeekend`, which is hardcoded to Saturday and Sunday.
import { isNonWorkingDay } from '../lib/workingDays';
import { leaveApi, dashboardApi, holidaysApi, blackoutsApi, settingsApi, usersApi } from '../lib/api';
import { formatLeave, unitLabel, resolveUnit } from '../lib/leaveUnits';
import { leaveTypeIconComponent } from '../lib/leaveTypeIcons';
import { iconOn, numberOn } from '../lib/leaveColour';
import { toast } from 'sonner';
import CancelLeaveDialog from '../components/CancelLeaveDialog';
import BrandedToaster from '../components/BrandedToaster';
import OtherLeaveTaken from '../components/OtherLeaveTaken';
// Time in lieu (DESIGN-toil.md J): its own balance, beside holiday.
import ToilCard from '../components/toil/ToilCard';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, getDay, isToday, parseISO } from 'date-fns';
import DateRangeField from '../components/DateRangeField';
import { NoLeaveDatesYours, NoLeaveDateActions, mine } from '../components/NoLeaveDatesYours';
import { approvedBy, bookedBy } from '../lib/bookingOutcome';
import { bookingLength, withTimes, backAtWork, dayShare } from '../lib/bookingTime';
import { dayPortion } from '../lib/dayPortion';
import TimedDayFill from '../components/TimedDayFill';
import { DayMark, DayNumberOver } from '../components/DayMark';

// ONE icon map for the whole app (lib/leaveTypeIcons) — the per-page copies
// only knew ~15 of the Settings picker's ~50 icons (Rob, 16 Jul).
const getLeaveTypeIcon = (iconName, className = "w-4 h-4 text-white") => {
  const IconComponent = leaveTypeIconComponent(iconName);
  return <IconComponent className={className} />;
};

// Component for rendering day cell with half-day support (year planner view)
const HalfDayCell = ({ leave, bankHoliday, isPending, children }) => {
  const dayPortion = leave?.day_portion || 'full';
  
  // If no leave or full day, render normally
  if (!leave || dayPortion === 'full') {
    return children;
  }
  
  // For half day, use diagonal clip-path
  const isMorning = dayPortion === 'morning';
  const icon = getLeaveTypeIcon(leave.icon, `w-3 h-3 ${iconOn(leave.color)}`);
  
  return (
    <div className="w-full h-full relative overflow-hidden rounded-sm" style={{ backgroundColor: leave.color + '26' }}>
      {/* The colored diagonal portion */}
      <div 
        className={`absolute inset-0 flex items-center justify-center ${
          isPending ? 'opacity-60' : ''
        }`}
        style={{ 
          backgroundColor: leave.color,
          clipPath: isMorning 
            ? 'polygon(0 0, 100% 0, 0 100%)' // Top-left triangle for morning
            : 'polygon(100% 0, 100% 100%, 0 100%)' // Bottom-right triangle for afternoon
        }}
      />
      {/* Dashed border for pending */}
      {isPending && (
        <div 
          className="absolute inset-0 border border-dashed rounded-sm"
          style={{ borderColor: leave.color }}
        />
      )}
      {/* Icon positioned in the colored area */}
      <div 
        className={`absolute flex items-center justify-center ${
          isMorning ? 'top-0 left-0' : 'bottom-0 right-0'
        }`}
        style={{ 
          width: '14px', 
          height: '14px',
        }}
      >
        {icon}
      </div>
    </div>
  );
};

const MyLeavePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [requests, setRequests] = useState([]);
  const [allowances, setAllowances] = useState([]);
  const [bankHolidays, setBankHolidays] = useState([]);
  const [takenByType, setTakenByType] = useState([]);
  const [bankHolidaysIncluded, setBankHolidaysIncluded] = useState(false);
  const [leaveYearStartMonth, setLeaveYearStartMonth] = useState(1);
  // The bank holidays of the BALANCE's leave year, for the figures printed beside it —
  // not `bankHolidays`, which is the calendar year the planner is showing (29/09/2026).
  const [balanceBankHolidays, setBalanceBankHolidays] = useState([]);
  const [balanceYear, setBalanceYear] = useState(null);
  const [blackouts, setBlackouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  // The planner shows the company's LEAVE year (Rob 6B, 30/10/2026), numbered like
  // LeaveAllowance.year. The first load moves to the leave year we're in — at an April company
  // in February that is LAST calendar year's number. Once only; after that the arrows decide.
  const yearSettled = useRef(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [quickBookDate, setQuickBookDate] = useState(null);
  const [quickBookLeaveTypeId, setQuickBookLeaveTypeId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);  // Full user profile with date_of_birth
  // ⚠️ WHICH DAYS THIS PERSON ACTUALLY WORKS — step 4, 07/08/2026. The calendar used to
  // ask date-fns `isWeekend()`, which is Saturday and Sunday for everybody on earth.
  // null here means "not loaded / couldn't tell", and `lib/workingDays` owns the one
  // Monday-to-Friday fallback. Do not default it here as well.
  const [workingDays, setWorkingDays] = useState(null);
  
  // Blackout dialog state
  const [blackoutDialogOpen, setBlackoutDialogOpen] = useState(false);
  // Rob 1C + 2A (02/11/2026): the box has an "Add new" and a "Yours" tab, and Change re-opens the form
  // on one you added. Which ones are yours AND changeable, the server says (`can_change`).
  const [blackoutTab, setBlackoutTab] = useState('add');
  const [editingBlackout, setEditingBlackout] = useState(null);
  const [blackoutSaving, setBlackoutSaving] = useState(false);
  const [canCreateBlackouts, setCanCreateBlackouts] = useState(false);
  const [blackoutWholeCompany, setBlackoutWholeCompany] = useState(false);
  const [allDepartments, setAllDepartments] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [blackoutForm, setBlackoutForm] = useState({
    name: '',
    reason: '',
    start_date: '',
    end_date: '',
    scope: '',  // Empty = "Choose option..."
    department_id: '',
    user_ids: [],  // Changed to array for multiple employees
    is_recurring: false
  });

  // Role-based access
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isManager = user?.role === 'manager' || isAdmin;

  useEffect(() => {
    fetchData();
  // Runs once on mount. `fetchData` is recreated every render, so listing it would re-fetch endlessly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // ⚠️ THE LEAVE YEAR'S START MONTH COMES FIRST (Rob 6B, 30/10/2026): it decides which dates
      // the planner asks for. It used to be fetched after everything else, for the balance only.
      const settingsRes = await settingsApi.get().catch(() => ({ data: null }));
      const _sm = (settingsRes.data && settingsRes.data.leave_year_start_month) || 1;
      setBankHolidaysIncluded(!!(settingsRes.data && settingsRes.data.bank_holidays_included));
      setLeaveYearStartMonth(_sm);
      if (!yearSettled.current) {
        yearSettled.current = true;
        const _thisLeaveYear = currentLeaveYear(_sm);
        if (_thisLeaveYear !== selectedYear) {
          setSelectedYear(_thisLeaveYear);  // fetches again, for that year
          return;
        }
      }
      const [startDate, endDate] = leaveYearRange(_sm, selectedYear);
      
      // Use user.id (not user_id) to filter leave requests for current user only
      const currentUserId = user?.id || user?.user_id;
      
      const requests = [
        leaveApi.listRequests({ user_id: currentUserId, start_date: startDate, end_date: endDate }),
        leaveApi.getMyAllowances(),
        // The planner's bank holidays: every calendar year the leave year touches, kept to the
        // ones inside it (the holidays API answers per calendar year).
        Promise.all(calendarYearsOf(_sm, selectedYear).map((y) => holidaysApi.list(y, user?.id)))
          .then((rs) => ({ data: rs.flatMap((r) => (r && r.data) || []).filter((h) => inLeaveYear(h.date, _sm, selectedYear)) })),
        blackoutsApi.list(false).catch(() => ({ data: [] })),
        usersApi.get(currentUserId).catch(() => ({ data: null })),  // Fetch full user profile
        // Their RESOLVED week — own pattern, else their location's, else the company's.
        // `usersApi.get` only carries their personal override, which is null for anyone
        // who simply follows their office, so it cannot answer this.
        usersApi.getSchedule(currentUserId).catch(() => ({ data: null })),
      ];
      
      if (isManager) {
        requests.push(dashboardApi.getPendingApprovals());
      }
      
      const results = await Promise.all(requests);

      // ⚠️ THE BALANCE'S LEAVE YEAR, which at an April company spans two calendar years —
      // so fetch each and keep the ones inside it, one per date (the server counts dates).
      const _annual = (results[1].data || []).find((a) => a.leave_type_name === 'Annual Leave');
      const _balYear = (_annual && _annual.year) || currentLeaveYear(_sm);
      const _lists = await Promise.all(calendarYearsOf(_sm, _balYear).map((y) => (
        holidaysApi.list(y, user?.id).catch(() => ({ data: [] })))));
      const _seen = new Set();
      setBalanceBankHolidays(_lists.flatMap((r) => (r && r.data) || [])
        .filter((h) => inLeaveYear(h.date, _sm, _balYear))
        .filter((h) => (_seen.has(h.date) ? false : _seen.add(h.date))));
      setBalanceYear(_balYear);

      // Leave taken per type (Sick / Unpaid / WFH…) for the "Other leave taken"
      // sidebar breakdown. Separate await so its failure never blanks the page.
      const takenRes = await leaveApi.getLeaveTakenByType({ year: selectedYear }).catch(() => ({ data: [] }));
      setTakenByType(takenRes.data || []);
      
      setRequests(results[0].data);
      setAllowances(results[1].data);
      setBankHolidays(results[2].data || []);
      setBlackouts(results[3].data || []);
      setUserProfile(results[4].data);  // Set full user profile
      setWorkingDays(results[5]?.data?.current?.working_days || null);
      if (isManager && results[5]) {
        setPendingCount(results[5].data.length);
      }
      
      // The No-leave dates box: may this person add them, "Everyone", and exactly which departments
      // and people. ⚠️ The SERVER answers, with the save's own rule (GET /blackouts/options,
      // 02/11/2026). It used to be worked out here — named-manager departments only — so a manager
      // whose reach comes from their own department saw empty lists the save would have accepted.
      if (isManager) {
        const opt = await blackoutsApi.options().catch(() => ({ data: null }));
        setCanCreateBlackouts(!!(opt.data && opt.data.can_create));
        setBlackoutWholeCompany(!!(opt.data && opt.data.whole_company));
        setAllDepartments((opt.data && opt.data.departments) || []);
        setAllEmployees((opt.data && opt.data.people) || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  // The cancel box (DESIGN-part-cancel.md): the whole booking or just some of its days.
  const [cancelId, setCancelId] = useState(null);
  const handleCancel = (requestId) => setCancelId(requestId);

  // Handler for quick book - opens dialog with pre-filled date and leave type
  const handleQuickBook = (date, leaveType) => {
    setQuickBookDate(date);
    setQuickBookLeaveTypeId(leaveType.id);
    setRequestDialogOpen(true);
  };

  // Handle creating a blackout
  const meId = user?.id || user?.user_id;
  const refreshBlackouts = async () => {
    const res = await blackoutsApi.list(false).catch(() => ({ data: null }));
    if (res.data) setBlackouts(res.data);
  };
  const openChangeBlackout = (b) => {
    setEditingBlackout(b);
    setBlackoutForm({
      name: b.name || '',
      reason: b.reason || '',
      start_date: b.start_date,
      end_date: b.end_date,
      scope: b.scope,
      department_id: b.department_id || '',
      user_ids: b.user_id ? [b.user_id] : [],
      is_recurring: !!b.is_recurring,
    });
    setBlackoutTab('add');
    setBlackoutDialogOpen(true);
  };
  const closeBlackoutBox = () => {
    setBlackoutDialogOpen(false);
    setBlackoutTab('add');
    if (editingBlackout) {
      setEditingBlackout(null);
      setBlackoutForm({ name: '', reason: '', start_date: '', end_date: '', scope: '', department_id: '', user_ids: [], is_recurring: false });
    }
  };

  const handleSaveBlackout = async () => {
    if (!blackoutForm.name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    if (!blackoutForm.start_date || !blackoutForm.end_date) {
      toast.error('Please select start and end dates');
      return;
    }
    if (!blackoutForm.scope) {
      toast.error('Please select who this applies to');
      return;
    }
    if (blackoutForm.scope === 'department' && !blackoutForm.department_id) {
      toast.error('Please select a department');
      return;
    }
    if (blackoutForm.scope === 'user' && blackoutForm.user_ids.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }
    
    setBlackoutSaving(true);
    try {
      if (editingBlackout) {
        // Changing one you added: the name, reason, dates and "every year" — not who it's for.
        await blackoutsApi.update(editingBlackout.id, {
          name: blackoutForm.name,
          reason: blackoutForm.reason || null,
          start_date: blackoutForm.start_date,
          end_date: blackoutForm.end_date,
          is_recurring: blackoutForm.is_recurring,
        });
        toast.success('No-leave dates changed');
        setEditingBlackout(null);
      // If scope is 'user' and multiple employees selected, create one blackout per employee
      } else if (blackoutForm.scope === 'user' && blackoutForm.user_ids.length > 0) {
        for (const userId of blackoutForm.user_ids) {
          const data = {
            name: blackoutForm.name,
            reason: blackoutForm.reason || null,
            start_date: blackoutForm.start_date,
            end_date: blackoutForm.end_date,
            scope: 'user',
            department_id: null,
            user_id: userId,
            is_recurring: blackoutForm.is_recurring
          };
          await blackoutsApi.create(data);
        }
        toast.success(`No-leave dates added for ${blackoutForm.user_ids.length} employee(s)`);
      } else {
        const data = {
          name: blackoutForm.name,
          reason: blackoutForm.reason || null,
          start_date: blackoutForm.start_date,
          end_date: blackoutForm.end_date,
          scope: blackoutForm.scope,
          department_id: blackoutForm.scope === 'department' ? blackoutForm.department_id : null,
          user_id: null,
          is_recurring: blackoutForm.is_recurring
        };
        await blackoutsApi.create(data);
        toast.success('No-leave dates added');
      }
      
      setBlackoutDialogOpen(false);
      setBlackoutForm({
        name: '',
        reason: '',
        start_date: '',
        end_date: '',
        scope: '',
        department_id: '',
        user_ids: [],
        is_recurring: false
      });
      
      // Refresh blackouts
      const res = await blackoutsApi.list(false);
      setBlackouts(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add the no-leave dates');
    } finally {
      setBlackoutSaving(false);
    }
  };

  // Generate months for the year
  const months = useMemo(() => {
    // The twelve months of the LEAVE year (Rob 6B, 30/10/2026) — April to March at an April company.
    return leaveYearMonths(leaveYearStartMonth, selectedYear);
  }, [selectedYear, leaveYearStartMonth]);

  // Create a map of dates to leave requests
  const leaveByDate = useMemo(() => {
    const map = {};
    requests.forEach(req => {
      if (req.status === 'cancelled' || req.status === 'declined') return;
      const start = new Date(req.start_date);
      const end = new Date(req.end_date);
      const days = eachDayOfInterval({ start, end });
      
      days.forEach((day, index) => {
        const key = format(day, 'yyyy-MM-dd');
        const isFirstDay = index === 0;
        const isLastDay = index === days.length - 1;
        
        // "full", "morning" or "afternoon" — one rule for both planners (lib/dayPortion)
        const day_portion = dayPortion(req, isFirstDay, isLastDay);
        
        map[key] = {
          type: req.leave_type_name,
          color: req.leave_type_color || '#f59e0b',
          icon: req.leave_type_icon || 'calendar',
          status: req.status,
          id: req.id,
          day_portion,
          // Additional details for popover
          start_date: req.start_date,
          end_date: req.end_date,
          // C16: the timed window, its cost and the owner's unit (lib/bookingTime).
          start_minute: req.start_minute,
          end_minute: req.end_minute,
          total_minutes: req.total_minutes,
          display_unit: req.display_unit,
          reason: req.reason,
          total_days: req.total_days,
          // The list endpoint sends the approver as actioned_by_name / actioned_at
          // (not approver_name) — reading the wrong keys is why the popover showed
          // no "Approved by". approval_chain carries multi-level sign-off.
          // H9: a request that approved itself has nobody's name to show.
          approver_name: approvedBy(req),
          approved_at: req.actioned_at,
          booked_by: bookedBy(req),
          approval_chain: req.approval_chain
        };
      });
    });
    return map;
  }, [requests]);

  // Create a map of bank holidays
  const bankHolidayByDate = useMemo(() => {
    const map = {};
    bankHolidays.forEach(h => {
      map[h.date] = h.name;
    });
    return map;
  }, [bankHolidays]);

  // Helper function to check if a date is blacked out for the current user
  const isBlackoutDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dateObj = new Date(dateStr);
    const userDepartmentId = user?.department_id;
    const userId = user?.id || user?.user_id;
    
    for (const blackout of blackouts) {
      const startDate = new Date(blackout.start_date);
      const endDate = new Date(blackout.end_date);
      
      // Check if date falls within blackout range
      if (dateObj >= startDate && dateObj <= endDate) {
        // Check scope
        if (blackout.scope === 'company') {
          return blackout;
        }
        if (blackout.scope === 'department' && blackout.department_id === userDepartmentId) {
          return blackout;
        }
        if (blackout.scope === 'user' && blackout.user_id === userId) {
          return blackout;
        }
      }
    }
    return null;
  };

  // Helper function to get all affected users for a blackout (aggregates same-name blackouts)
  const getBlackoutAffectedUsers = (blackout) => {
    if (blackout.scope !== 'user') return null;
    
    // Find all blackouts with the same name and date range
    const relatedBlackouts = blackouts.filter(b => 
      b.scope === 'user' && 
      b.name === blackout.name && 
      b.start_date === blackout.start_date && 
      b.end_date === blackout.end_date
    );
    
    if (relatedBlackouts.length <= 1) return blackout.user_name;
    
    // Return comma-separated list of names
    return relatedBlackouts
      .map(b => b.user_name)
      .filter(Boolean)
      .join(', ');
  };

  // The legend: the leave types on this year, in their own colours, and "Pending" only when
  // something is (as Team calendar). It said "Approved Leave" in green, whatever the colours were.
  const legend = useMemo(() => {
    const [from, to] = leaveYearRange(leaveYearStartMonth, selectedYear);
    const types = new Map();
    let pending = false;
    requests.forEach((r) => {
      if (r.status === 'cancelled' || r.status === 'declined') return;
      if (String(r.start_date).slice(0, 10) > to || String(r.end_date).slice(0, 10) < from) return;
      if (!types.has(r.leave_type_name)) types.set(r.leave_type_name, r.leave_type_color || '#f59e0b');
      if (r.status === 'pending') pending = true;
    });
    return { types: Array.from(types.entries()), pending };
  }, [requests, leaveYearStartMonth, selectedYear]);

  // Calculate summary stats
  const summary = useMemo(() => {
    const annualLeave = allowances.find(a => a.leave_type_name === 'Annual Leave') || {};
    const totalAllowance = annualLeave.total_allowance || 0;
    const used = annualLeave.used || 0;
    const pending = annualLeave.pending || 0;
    const remaining = annualLeave.remaining || 0;
    // Carried-over days are part of what can be taken (DESIGN-rollover.md). They were never
    // listed here, so a balance with carry-over didn't add up on this panel (09/10/2026).
    const carried = annualLeave.carried_over || 0;

    // Display unit + canonical minutes, straight off the allowance row. `unit` is a
    // rendering choice only — every figure above is unchanged by it. `deductions`
    // (used + pending) is summed in MINUTES rather than by converting the day floats,
    // so the hours figure is the server's arithmetic, not ours (D1).
    const mins = {
      total: annualLeave.total_allowance_minutes,
      used: annualLeave.used_minutes,
      pending: annualLeave.pending_minutes,
      remaining: annualLeave.remaining_minutes,
      carried: annualLeave.carried_over_minutes,
    };
    const totalWithCarryMinutes =
      (mins.total === null || mins.total === undefined || (carried && (mins.carried === null || mins.carried === undefined)))
        ? null
        : mins.total + (mins.carried || 0);
    const deductionsMinutes =
      (mins.used === null || mins.used === undefined || mins.pending === null || mins.pending === undefined)
        ? null
        : mins.used + mins.pending;

    // ONE unit for this whole panel. It shows five figures side by side, so a row that
    // is only half-backfilled must render all of them in days rather than a mix.
    const unit = resolveUnit(
      annualLeave.display_unit, mins.total, totalWithCarryMinutes, mins.used, mins.pending, mins.remaining, deductionsMinutes);


    // Calculate time off by month (booked leave)
    const timeOffByMonth = Array(12).fill(0);
    requests.forEach(req => {
      if (req.status !== 'approved') return;
      const start = new Date(req.start_date);
      const end = new Date(req.end_date);
      const days = eachDayOfInterval({ start, end });
      days.forEach(day => {
        if (inLeaveYear(format(day, 'yyyy-MM-dd'), leaveYearStartMonth, selectedYear)) {
          timeOffByMonth[leaveYearMonthIndex(leaveYearStartMonth, day)] += 1;
        }
      });
    });
    
    // Calculate bank holidays by month
    // Only count bank holidays if the user does NOT work public holidays
    const userWorksPublicHolidays = user?.works_public_holidays || false;
    const bankHolidaysByMonth = Array(12).fill(0);
    
    if (!userWorksPublicHolidays) {
      bankHolidays.forEach(h => {
        const holidayDate = new Date(h.date);
        if (inLeaveYear(h.date, leaveYearStartMonth, selectedYear)) {
          bankHolidaysByMonth[leaveYearMonthIndex(leaveYearStartMonth, holidayDate)] += 1;
        }
      });
    }
    
    // Calculate bank holiday breakdown (only relevant if user doesn't work bank
    // holidays) — and only those within their employment (a mid-year joiner
    // didn't "get" bank holidays that passed before they started).
    const today = new Date();
    const _startDate = user?.start_date ? new Date(user.start_date) : null;
    // ⚠️ The BALANCE's leave year (29/09/2026) — see `balanceBankHolidays`.
    const effectiveBankHolidays = (userWorksPublicHolidays ? [] : balanceBankHolidays)
      .filter(h => !_startDate || new Date(h.date) >= _startDate);
    const bankHolidaysPassed = effectiveBankHolidays.filter(h => new Date(h.date) < today).length;
    const bankHolidaysRemaining = effectiveBankHolidays.filter(h => new Date(h.date) >= today).length;
    const nextBankHoliday = effectiveBankHolidays
      .filter(h => new Date(h.date) >= today)
      .sort((a, b) => new Date(a.date) - new Date(b.date))[0];
    
    return {
      totalAllowance,
      carried,
      totalWithCarryMinutes,
      used,
      pending,
      remaining,
      unit,
      mins,
      deductionsMinutes,
      timeOffByMonth,
      bankHolidaysByMonth,
      bankHolidayCount: effectiveBankHolidays.length,
      bankHolidaysPassed,
      bankHolidaysRemaining,
      nextBankHoliday,
      userWorksPublicHolidays
    };
  }, [allowances, requests, bankHolidays, balanceBankHolidays, selectedYear, leaveYearStartMonth, user?.works_public_holidays, user?.start_date]);

  // Render a single month calendar
  const renderMonth = (month) => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Calculate offset for first day of month (Monday = 0, Sunday = 6)
    const firstDayOfWeek = getDay(monthStart);
    const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    return (
      <div key={month.toISOString()} className="rounded-2xl border border-hive-line-soft dark:border-border p-3">
        <h3 className="font-heading font-semibold text-[15px] mb-2 px-0.5">
          {format(month, 'MMMM')}
        </h3>
        
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-[3px] mb-1">
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
            <div key={i} className="text-[10.5px] text-center font-semibold text-muted-foreground">
              {d}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-[3px]">
          {/* Empty cells for offset */}
          {Array(offset).fill(null).map((_, i) => (
            <div key={`empty-${i}`} className="h-8 sm:h-auto sm:aspect-square" />
          ))}
          
          {/* Day cells */}
          {days.map(day => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const leave = leaveByDate[dateStr];
            // First working day back after the leave ends (skip weekends), for the
            // popover's "Back at work" line — mirrors the dashboard wallchart.
            // ⚠️ THEIR next working day, not the next weekday. Somebody in Algiers whose
            // leave ends on a Thursday is back on SUNDAY.
            // ⚠️ A TIMED booking is back the SAME day at its end time (lib/bookingTime, C16).
            const back = leave ? backAtWork(leave, workingDays) : null;
            const leaveReturnDate = back ? back.date : null;
            const bankHoliday = bankHolidayByDate[dateStr];
            const weekend = isNonWorkingDay(day, workingDays);
            const today = isToday(day);
            const isPending = leave?.status === 'pending';
            const isHalfDay = leave && leave.day_portion !== 'full';
            const isMorning = leave?.day_portion === 'morning';
            const timedShare = leave ? dayShare(leave) : null;  // C16: null unless timed
            
            // Check for blackout
            const blackout = isBlackoutDate(day);
            const isBlackedOut = !!blackout && !leave && !bankHoliday && !weekend;
            
            // Check for birthday - compare month and day only
            const isBirthday = userProfile?.date_of_birth && (() => {
              const dob = new Date(userProfile.date_of_birth);
              return dob.getMonth() === day.getMonth() && dob.getDate() === day.getDate();
            })();
            
            let icon = null;
            let title = '';
            
            if (leave) {
              title = `${leave.type} (${leave.status})${isHalfDay ? ` - ${isMorning ? 'AM' : 'PM'}` : ''}`;
              icon = getLeaveTypeIcon(leave.icon, `w-4 h-4 ${iconOn(leave.color)}`);
            } else if (bankHoliday) {
              title = bankHoliday;
              icon = <Landmark className="w-4 h-4 text-white" />;
            } else if (isBirthday) {
              title = 'Your Birthday! 🎉';
            } else if (blackout) {
              title = `No-leave dates: ${blackout.name}`;
            }
            
            // Check if day is in the past (can't book leave for past dates)
            const isPast = day < new Date(new Date().setHours(0, 0, 0, 0));
            const canQuickBook = !leave && !bankHoliday && !isPast && !isBlackedOut && !isBirthday;
            
            return (
              <div
                key={dateStr}
                className={`
                  hh-day h-8 sm:h-auto sm:aspect-square flex items-center justify-center text-[11.5px] font-semibold rounded-[5px] relative overflow-hidden
                  ${weekend && !leave && !bankHoliday ? 'bg-hive-weekend dark:bg-background/80 text-muted-foreground' : 'bg-hive-tile dark:bg-secondary/60'}
                  ${today && !leave && !bankHoliday ? '!bg-primary !text-primary-foreground' : ''}
                  ${today ? 'ring-2 ring-primary ring-offset-1 ring-offset-card' : ''}
                `}
                title={!leave && !isBlackedOut ? title : undefined}
              >
                {leave ? (
                  // Leave cell with hover card for details
                  <HoverCard openDelay={200} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <div 
                        className="w-full h-full cursor-pointer hover:ring-1 hover:ring-white/30 transition-all rounded-sm"
                        style={leave.status !== 'pending' && !isHalfDay && timedShare === null ? { backgroundColor: leave.color } : {}}
                      >
                        {timedShare !== null ? (
                          // C16: a timed booking is part of a day — same drawing as the wallchart.
                          <div className="w-full h-full relative overflow-hidden rounded-sm">
                            <TimedDayFill color={leave.color} share={timedShare} pending={isPending} iconClass="w-2.5 h-2.5" n={day.getDate()} />
                          </div>
                        ) : isHalfDay ? (
                          // Half-day rendering with diagonal
                          <div className="w-full h-full relative overflow-hidden rounded-sm">
                            {/* The colored diagonal portion */}
                            <div 
                              className={`absolute inset-0 ${isPending ? 'opacity-60' : ''}`}
                              style={{ 
                                backgroundColor: leave.color,
                                clipPath: isMorning 
                                  ? 'polygon(0 0, 100% 0, 0 100%)' // Top-left triangle for morning
                                  : 'polygon(100% 0, 100% 100%, 0 100%)' // Bottom-right triangle for afternoon
                              }}
                            />
                            {/* Dashed border for pending */}
                            {isPending && (
                              <div 
                                className="absolute inset-0 border border-dashed rounded-sm"
                                style={{ borderColor: leave.color }}
                              />
                            )}
                            {/* Icon positioned in the colored area */}
                            <div 
                              className={`hh-day-icon absolute flex items-center justify-center ${
                                isMorning ? 'top-0 left-0' : 'bottom-0 right-0'
                              }`}
                              style={{ width: '14px', height: '14px' }}
                            >
                              {getLeaveTypeIcon(leave.icon, `w-3 h-3 ${iconOn(leave.color)}`)}
                            </div>
                            <DayNumberOver n={day.getDate()} numClass="text-foreground hh-day-num-halo" />
                          </div>
                        ) : (
                          // Full day rendering
                          <div className={`w-full h-full flex items-center justify-center rounded-sm ${isPending ? 'bg-amber-500/30 border border-dashed border-amber-500' : ''}`}
                               style={!isPending ? { backgroundColor: leave.color } : {}}>
                            <DayMark n={day.getDate()} numClass={isPending ? 'text-foreground' : numberOn(leave.color)}>{icon}</DayMark>
                          </div>
                        )}
                      </div>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-72 p-0" align="start">
                      <div className="p-3 border-b border-border" style={{ backgroundColor: leave.color + '20' }}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm">{leave.type}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            leave.status === 'approved' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {leave.status === 'approved' ? 'Approved' : 'Pending'}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{user?.first_name} {user?.last_name}</p>
                      </div>
                      <div className="p-3 space-y-3">
                        {/* Dates */}
                        <div className="flex items-start gap-2">
                          <CalendarCheck className="w-4 h-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">Duration</p>
                            <p className="text-sm">
                              {leave.start_date && leave.end_date ? (
                                leave.start_date === leave.end_date 
                                  ? withTimes(format(parseISO(leave.start_date), 'dd MMM yyyy'), leave)
                                  : `${format(parseISO(leave.start_date), 'dd MMM')} - ${format(parseISO(leave.end_date), 'dd MMM yyyy')}`
                              ) : dateStr}
                              {!!leave.total_days && (
                                <span className="text-muted-foreground ml-1">({bookingLength(leave)})</span>
                              )}
                            </p>
                          </div>
                        </div>

                        {/* Back at work */}
                        {leaveReturnDate && (
                          <div className="flex items-start gap-2">
                            <Plane className="w-4 h-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Back at work</p>
                              <p className="text-sm">
                                {format(leaveReturnDate, 'EEE dd MMM yyyy')}
                                {back && back.time ? `, from ${back.time}` : ''}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Reason */}
                        {leave.reason && (
                          <div className="flex items-start gap-2">
                            <FileText className="w-4 h-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Reason</p>
                              <p className="text-sm">{leave.reason}</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Booked by an API key (DESIGN-api-and-webhooks.md step 3, Rob's E A). Only ever
                            there for a booking a key made: the app never said who booked, and this doesn't start. */}
                        {leave.booked_by && (
                          <div className="flex items-start gap-2" data-testid="leave-booked-by">
                            <KeyRound className="w-4 h-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Booked by</p>
                              <p className="text-sm">{leave.booked_by}</p>
                            </div>
                          </div>
                        )}

                        {/* Approver - only for approved requests */}
                        {leave.status === 'approved' && leave.approver_name && (
                          <div className="flex items-start gap-2">
                            <CircleCheck className="w-4 h-4 text-green-500 mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Approved by</p>
                              <p className="text-sm">
                                {leave.approver_name}
                                {leave.approved_at && (
                                  <span className="text-muted-foreground text-xs ml-1">
                                    on {format(parseISO(leave.approved_at), 'dd MMM yyyy')}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {/* Pending indicator */}
                        {leave.status === 'pending' && (
                          <div className="flex items-start gap-2">
                            <CircleDashed className="w-4 h-4 text-yellow-500 mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Status</p>
                              <p className="text-sm text-yellow-400">Awaiting approval</p>
                            </div>
                          </div>
                        )}

                        {/* Cancel — this is the owner's own leave, so they can always
                            withdraw it (matches the dashboard wallchart's action). */}
                        {leave.id && leave.status !== 'cancelled' && (
                          <button
                            onClick={() => handleCancel(leave.id)}
                            className="w-full mt-1 flex items-center justify-center gap-1.5 text-xs font-medium text-red-500 hover:text-red-600 hover:bg-red-500/10 rounded-md py-1.5 transition-colors border-t border-border pt-2"
                          >
                            <X className="w-3.5 h-3.5" />
                            Cancel this leave
                          </button>
                        )}
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                ) : bankHoliday ? (
                  <div className="w-full h-full flex items-center justify-center rounded-sm"
                       style={{ backgroundColor: 'rgb(168, 85, 247)' }}>
                    <DayMark n={day.getDate()} numClass="text-white">{icon}</DayMark>
                  </div>
                ) : isBlackedOut ? (
                  // Blackout cell
                  <HoverCard openDelay={200} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <div 
                        className="w-full h-full flex items-center justify-center rounded-sm cursor-help"
                        style={{ 
                          background: 'repeating-linear-gradient(45deg, rgba(239,68,68,0.15), rgba(239,68,68,0.15) 3px, rgba(239,68,68,0.3) 3px, rgba(239,68,68,0.3) 6px)',
                          border: '1px solid rgba(239,68,68,0.4)'
                        }}
                      >
                        <DayMark n={day.getDate()} numClass="text-hive-danger-fg dark:text-red-300"><XCircle className="w-4 h-4 text-red-400" /></DayMark>
                      </div>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-56 p-0" align="start">
                      <div className="p-2 border-b border-red-500/30 bg-red-500/10">
                        <span className="font-semibold text-xs text-red-400">🚫 No-leave dates</span>
                      </div>
                      <div className="p-2 space-y-1">
                        <p className="font-medium text-xs">{blackout.name}</p>
                        {blackout.reason && (
                          <p className="text-[10px] text-muted-foreground">{blackout.reason}</p>
                        )}
                        <div className="text-[10px]">
                          <span className="text-muted-foreground">Applies to: </span>
                          <span className="font-medium">
                            {blackout.scope === 'company' && 'Everyone (Company-wide)'}
                            {blackout.scope === 'department' && (blackout.department_name || 'Department')}
                            {blackout.scope === 'user' && (getBlackoutAffectedUsers(blackout) || 'Specific Employee(s)')}
                          </span>
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                          {new Date(blackout.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          {' - '}
                          {new Date(blackout.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </p>
                        <p className="text-[10px] text-red-400">Leave cannot be booked</p>
                        <NoLeaveDateActions blackout={blackout} meId={meId} onChange={openChangeBlackout} onRemoved={refreshBlackouts} />
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                ) : isBirthday && !leave ? (
                  (() => {
                    const canBookHere = !bankHoliday && !isPast && !isBlackedOut;
                    const ribbon = (
                      <>
                        <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[8px]" style={{ background: 'linear-gradient(90deg,#b8862f,#f6e08f 42%,#fffbe6 50%,#f6e08f 58%,#b8862f)', boxShadow: '0 0 0 1px rgba(0,0,0,0.12)' }} />
                        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[8px]" style={{ background: 'linear-gradient(0deg,#b8862f,#f6e08f 42%,#fffbe6 50%,#f6e08f 58%,#b8862f)', boxShadow: '0 0 0 1px rgba(0,0,0,0.12)' }} />
                        <span className="hh-day-icon absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[20px] leading-none drop-shadow">🎀</span>
                        <DayNumberOver n={day.getDate()} numClass="text-white hh-day-num-dark-halo" />
                      </>
                    );
                    if (!canBookHere) return (
                      <div className="relative w-full h-full rounded-sm overflow-hidden" style={{ background: 'linear-gradient(135deg,#ef7a7a,#dc5f5f)' }} title="Your Birthday! 🎉">
                        {ribbon}
                      </div>
                    );
                    return (
                      <QuickBookPopover date={day} onSelectLeaveType={handleQuickBook}>
                        <button className="relative w-full h-full rounded-sm overflow-hidden hover:ring-2 hover:ring-white/70 transition-all" style={{ background: 'linear-gradient(135deg,#ef7a7a,#dc5f5f)' }} title="Your Birthday! 🎉 \u2014 click to book leave">
                          {ribbon}
                        </button>
                      </QuickBookPopover>
                    );
                  })()
                ) : canQuickBook ? (
                  <QuickBookPopover date={day} onSelectLeaveType={handleQuickBook}>
                    <button className="w-full h-full flex items-center justify-center hover:bg-primary/10 rounded-sm transition-colors">
                      {day.getDate()}
                    </button>
                  </QuickBookPopover>
                ) : (
                  <span className={isPast ? 'text-muted-foreground/50' : ''}>{day.getDate()}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      
      {/* Shared Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <header className="relative z-30">
          <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3 px-4 sm:px-6 lg:px-8 pt-6 pb-1">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <div className="min-w-0">
                <h1 className="font-heading text-2xl sm:text-[30px] sm:leading-tight font-semibold">My leave</h1>
                <p className="text-sm text-muted-foreground mt-0.5">Your year at a glance</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canCreateBlackouts && (
                <Button 
                  variant="outline"
                  className="bg-card text-foreground hover:bg-secondary"
                  onClick={() => setBlackoutDialogOpen(true)}
                  data-testid="block-leave-btn"
                >
                  <Ban className="w-4 h-4 mr-2 text-hive-danger-fg" />
                  No-leave dates
                </Button>
              )}
              <Button 
                className="btn-primary"
                onClick={() => setRequestDialogOpen(true)}
                data-testid="request-leave-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Request leave
              </Button>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:px-8 space-y-5">
          <div className="hh-planner">
            {/* Year Calendar Grid */}
            <div className="hh-planner-year card-hive p-5">
              {/* Year Navigation */}
              <div className="flex items-center gap-3 mb-4">
                <button 
                  onClick={() => setSelectedYear(y => y - 1)}
                  className="w-9 h-9 rounded-full border border-hive-line dark:border-border bg-card flex items-center justify-center hover:bg-secondary"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="font-heading font-semibold text-lg px-2">
                  {leaveYearSpan(leaveYearStartMonth, selectedYear)}
                </span>
                <button 
                  onClick={() => setSelectedYear(y => y + 1)}
                  className="w-9 h-9 rounded-full border border-hive-line dark:border-border bg-card flex items-center justify-center hover:bg-secondary"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-24">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 min-[1440px]:grid-cols-6 gap-3">
                  {months.map(month => renderMonth(month))}
                </div>
              )}
              
              {/* Legend — the leave types actually on this year, in their own colours (as Team calendar). */}
              <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 pt-3 border-t border-border text-[12.5px] text-muted-foreground">
                {bankHolidays.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-purple-500" />
                    <span>Bank holiday</span>
                  </div>
                )}
                {blackouts.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ 
                        background: 'repeating-linear-gradient(45deg, rgba(239,68,68,0.15), rgba(239,68,68,0.15) 2px, rgba(239,68,68,0.35) 2px, rgba(239,68,68,0.35) 4px)',
                        border: '1px solid rgba(239,68,68,0.5)'
                      }}
                    />
                    <span>No-leave dates</span>
                  </div>
                )}
                {legend.types.map(([name, color]) => (
                  <div key={name} className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: color }} />
                    <span>{name}</span>
                  </div>
                ))}
                {legend.pending && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded border-2 border-dashed border-muted-foreground opacity-60" />
                    <span>Pending</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 rounded bg-primary" />
                  <span>Today</span>
                </div>
              </div>
            </div>

            {/* Anything you're waiting on a decision for — under the year on a computer, at the top on
                a phone (Rob 3A, 30/10/2026: above the year it pushed it down ~350 px).
                Until 01/08/2026 this page showed pending requests ONLY as dashed cells
                on the year grid — you couldn't see how many were waiting, who had them,
                or which step they'd reached. Shared with the dashboard so the two can't
                drift. */}
            <div className="hh-planner-pending">
            <YourPendingRequests
              requests={requests}
              userId={user?.id || user?.user_id}
              onCancel={handleCancel}
              onChanged={fetchData}
            />
            </div>
            
            {/* Right Sidebar Summary */}
            <div className="hh-planner-side">
              {/* Year End Summary Card — portrait beside the year on a big screen, landscape under it
                  on a laptop (Rob 2A + "landscape", 30/10/2026; index.css .hh-planner). */}
              <div className="hh-planner-balance card-hive p-4">
                <h3 className="font-heading font-semibold mb-4 text-[15px] text-foreground">
                  {/* The year of the BALANCE below — not the calendar being shown, which pages
                      separately (Rob, 29/09/2026; was "Year end Dec {selectedYear}"). */}
                  {leaveYearHeading(leaveYearStartMonth,
                    (allowances.find((a) => a.leave_type_name === 'Annual Leave') || {}).year
                    || currentLeaveYear(leaveYearStartMonth))}
                </h3>
                
                <div className="hh-balance-body">
                <div>
                {/* Allowance Section.
                    Every figure is rendered through formatLeave with the employee's own
                    unit. The column header follows the unit too — a "Days" header over a
                    column of "74h 15m" is how you get a support ticket. */}
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Allowance</span>
                    <span className="font-medium capitalize">{unitLabel({ unit: summary.unit })}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Annual allowance</span>
                    <span className="font-medium">{formatLeave(summary.totalAllowance, { unit: summary.unit, minutes: summary.mins.total, withUnit: false })}</span>
                  </div>
                  {summary.carried !== 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Carried over from last year</span>
                      <span className="font-medium">{formatLeave(summary.carried, { unit: summary.unit, minutes: summary.mins.carried, withUnit: false })}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-semibold border-t border-border pt-2 mt-2">
                    <span>Total allowance</span>
                    <span>{formatLeave(summary.totalAllowance + summary.carried, { unit: summary.unit, minutes: summary.totalWithCarryMinutes, withUnit: false })}</span>
                  </div>
                </div>

                {/* Deductions Section */}
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Deductions</span>
                    <span className="font-medium capitalize">{unitLabel({ unit: summary.unit })}</span>
                  </div>
                  <div className="flex justify-between text-sm items-center">
                    <span className="flex items-center gap-2">
                      <Palmtree className="w-4 h-4 text-green-500" />
                      Holiday
                    </span>
                    <span className="font-medium">{formatLeave(summary.used, { unit: summary.unit, minutes: summary.mins.used, withUnit: false })}</span>
                  </div>
                  {summary.pending > 0 && (
                    <div className="flex justify-between text-sm items-center text-hive-pending-fg dark:text-hive-orange">
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Pending
                      </span>
                      <span className="font-medium">{formatLeave(summary.pending, { unit: summary.unit, minutes: summary.mins.pending, withUnit: false })}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-semibold border-t border-border pt-2 mt-2">
                    <span>Total deductions</span>
                    <span>{formatLeave(summary.used + summary.pending, { unit: summary.unit, minutes: summary.deductionsMinutes, withUnit: false })}</span>
                  </div>
                </div>

                </div>
                <div>
                {/* Remaining */}
                <div className="bg-hive-navy dark:bg-[#1C2B52] text-white rounded-2xl p-4 text-center mb-4">
                  <p className="text-sm text-hive-on-navy mb-1 capitalize">
                    {unitLabel({ unit: summary.unit })} remaining
                  </p>
                  {/* `remaining` is ALREADY net of bank holidays (D8 — deducted from the
                      pot server-side, once). Subtracting them again here, as this used
                      to, now DOUBLE-COUNTS. */}
                  <p className="text-4xl font-heading font-bold text-hive-orange">{formatLeave(summary.remaining, { unit: summary.unit, minutes: summary.mins.remaining, withUnit: false })}</p>
                  {!summary.userWorksPublicHolidays && summary.bankHolidaysRemaining > 0 && (
                    <p className="text-xs text-hive-muted-cool mt-2">
                      Plus <span className="text-purple-300 font-medium">{summary.bankHolidaysRemaining}</span> bank holiday {summary.bankHolidaysRemaining === 1 ? 'day' : 'days'}
                    </p>
                  )}
                  {/* Explain the number rather than leaving people to guess. Bank holidays
                      now come OUT of the pot (D8), so an employee on an "included" policy
                      sees fewer bookable days than their headline allowance — which is
                      correct, but baffling without a word of explanation. */}
                  {!summary.userWorksPublicHolidays && (
                    <p className="text-[11px] text-hive-muted-cool mt-2 leading-snug">
                      {bankHolidaysIncluded
                        ? <>Your allowance of {formatLeave(summary.totalAllowance, { unit: summary.unit, minutes: summary.mins.total })} <strong>includes</strong> bank holidays, so this is what you can choose freely.</>
                        : <>Bank holidays are given <strong>on top</strong> of your allowance.</>}
                    </p>
                  )}
                  {/* How the number was worked out — words, not a grey "?" (Rob C, 01/11/2026). */}
                  <div className="mt-3">
                    <AllowanceExplainer withWords />
                  </div>
                </div>
                {/* Carry-over use-by (DESIGN-rollover.md, 09/10/2026) — only before the date. */}
                <CarryOverLine allowance={allowances.find((a) => a.leave_type_name === 'Annual Leave')} className="mb-4 !mt-0" />
                {/* DESIGN-leave-years.md decision H (option B): only when next leave year has leave in it. */}
                <NextLeaveYear reloadKey={allowances} className="mb-4" />
                
                </div>
                {/* Non-deductible leave - Bank Holidays Section */}
                <div className="space-y-3 border-t border-border pt-4">
                  <p className="text-sm text-muted-foreground font-medium">Bank holidays ({leaveYearLabel(leaveYearStartMonth, balanceYear || currentLeaveYear(leaveYearStartMonth))})</p>
                  
                  {summary.userWorksPublicHolidays ? (
                    // User works on bank holidays - show notice
                    <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                      <p className="text-sm text-amber-400 font-medium">You work on bank holidays</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Bank holidays don't apply to your schedule. Speak to your manager if this is incorrect.
                      </p>
                    </div>
                  ) : (
                    // User has bank holidays off - show breakdown
                    <>
                      {/* Total Bank Holidays */}
                      <div className="flex justify-between text-sm items-center">
                        <span className="flex items-center gap-2">
                          <Landmark className="w-4 h-4 text-purple-500" />
                          Total this leave year
                        </span>
                        <span className="font-semibold text-purple-700 dark:text-purple-300">{summary.bankHolidayCount} {summary.bankHolidayCount === 1 ? 'day' : 'days'}</span>
                      </div>
                      
                      {/* Bank Holidays Passed */}
                      <div className="flex justify-between text-sm items-center text-muted-foreground">
                        <span className="flex items-center gap-2 pl-6">
                          Already passed
                        </span>
                        <span>{summary.bankHolidaysPassed} {summary.bankHolidaysPassed === 1 ? 'day' : 'days'}</span>
                      </div>
                      
                      {/* Bank Holidays Remaining */}
                      <div className="flex justify-between text-sm items-center">
                        <span className="flex items-center gap-2 pl-6">
                          Still to come
                        </span>
                        <span className="font-medium text-hive-success-fg dark:text-emerald-300">{summary.bankHolidaysRemaining} {summary.bankHolidaysRemaining === 1 ? 'day' : 'days'}</span>
                      </div>
                      
                      {/* Next Bank Holiday */}
                      {summary.nextBankHoliday && (
                        <div className="mt-3 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                          <p className="text-xs text-muted-foreground mb-1">Next bank holiday</p>
                          <p className="font-medium text-sm">{summary.nextBankHoliday.name}</p>
                          <p className="text-xs text-purple-700 dark:text-purple-300">
                            {format(new Date(summary.nextBankHoliday.date), 'EEEE, d MMMM yyyy')}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Other (non-deducting) leave taken — Sick / Unpaid / WFH… */}
                <OtherLeaveTaken items={takenByType} unit={summary.unit} year={leaveYearLabel(leaveYearStartMonth, selectedYear)} />
                </div>
              </div>

              <div className="hh-planner-more">
              {/* Time in lieu — its own balance (DESIGN-toil.md A, J). Nothing when off. */}
              <ToilCard refreshKey={allowances} />

              {/* Time Off by Month Chart - Stacked Bar */}
              <div className="card-hive p-4">
                <h3 className="font-heading font-semibold mb-4 text-[15px] text-foreground">
                  Time off
                </h3>
                {(() => {
                  // Calculate max total (leave + bank holidays) for scaling
                  const totalByMonth = summary.timeOffByMonth.map((leave, i) => leave + summary.bankHolidaysByMonth[i]);
                  const maxDays = Math.max(...totalByMonth, 1);
                  
                  return (
                    <div className="flex items-end justify-between h-20 gap-1">
                      {summary.timeOffByMonth.map((leaveDays, i) => {
                        const bankHolidayDays = summary.bankHolidaysByMonth[i];
                        const totalDays = leaveDays + bankHolidayDays;
                        const leaveHeight = totalDays > 0 ? (leaveDays / maxDays) * 100 : 0;
                        const bankHolidayHeight = totalDays > 0 ? (bankHolidayDays / maxDays) * 100 : 0;
                        
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                            {/* Stacked bar container */}
                            <div 
                              className="w-full flex flex-col justify-end"
                              style={{ height: totalDays > 0 ? `${((totalDays) / maxDays) * 100}%` : '2px' }}
                              title={`${leaveDays} leave + ${bankHolidayDays} bank holiday${bankHolidayDays !== 1 ? 's' : ''}`}
                            >
                              {/* Bank holiday portion (purple) - on top */}
                              {bankHolidayDays > 0 && (
                                <div 
                                  className="w-full bg-purple-500 rounded-t"
                                  style={{ 
                                    flex: `0 0 ${(bankHolidayDays / totalDays) * 100}%`,
                                    minHeight: '4px'
                                  }}
                                />
                              )}
                              {/* Leave portion (amber/primary) - on bottom */}
                              {leaveDays > 0 && (
                                <div 
                                  className={`w-full bg-primary ${bankHolidayDays === 0 ? 'rounded-t' : ''}`}
                                  style={{ 
                                    flex: `0 0 ${(leaveDays / totalDays) * 100}%`,
                                    minHeight: '4px'
                                  }}
                                />
                              )}
                              {/* Empty bar placeholder */}
                              {totalDays === 0 && (
                                <div className="w-full bg-border/50 rounded-t" style={{ height: '2px' }} />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
                <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
                  {leaveYearInitials(leaveYearStartMonth).map((m, i) => (
                    <span key={i} className="flex-1 text-center">{m}</span>
                  ))}
                </div>
                {/* Legend */}
                <div className="flex items-center justify-center gap-4 mt-3 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-primary rounded" />
                    <span className="text-muted-foreground">Leave</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-purple-500 rounded" />
                    <span className="text-muted-foreground">Bank holidays</span>
                  </div>
                </div>
              </div>
              
              {/* Upcoming Leave Requests */}
              <div className="card-hive p-4">
                <h3 className="font-heading font-semibold mb-4 text-[15px] text-foreground">
                  Upcoming leave
                </h3>
                {requests.filter(r => r.status !== 'cancelled' && r.status !== 'declined' && new Date(r.start_date) >= new Date()).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No upcoming leave</p>
                ) : (
                  <div className="space-y-3">
                    {requests
                      .filter(r => r.status !== 'cancelled' && r.status !== 'declined' && new Date(r.start_date) >= new Date())
                      .slice(0, 5)
                      .map(req => (
                        <div key={req.id} className="flex items-center gap-3 group">
                          <div 
                            className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${req.leave_type_color || '#f59e0b'}20` }}
                          >
                            <Palmtree className="w-4 h-4" style={{ color: req.leave_type_color || '#f59e0b' }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{req.leave_type_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {withTimes(format(new Date(req.start_date), 'd MMM'), req)}
                              {req.start_date !== req.end_date && ` - ${format(new Date(req.end_date), 'd MMM')}`}
                            </p>
                            {/* Approval progress for multi-level requests */}
                            {req.approval_chain && req.approval_chain.length > 1 && req.status === 'pending' && (
                              <div className="flex items-center gap-1 mt-1">
                                {req.approval_chain.map((step, idx) => (
                                  <span 
                                    key={idx}
                                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                                      step.status === 'APPROVED' 
                                        ? 'bg-green-500/20 text-green-600' 
                                        : step.status === 'PENDING' 
                                          ? 'bg-amber-500/20 text-amber-600' 
                                          : 'bg-red-500/20 text-red-600'
                                    }`}
                                    title={step.approver_name ? `${step.status} by ${step.approver_name}` : step.status}
                                  >
                                    {step.status === 'APPROVED' ? '✓' : step.status === 'PENDING' ? '⏳' : '✗'} {step.level_name || `L${step.level_order}`}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            req.status === 'approved' ? 'bg-hive-success-bg text-hive-success-fg dark:bg-emerald-500/20 dark:text-emerald-300' : 'bg-hive-pending-bg text-hive-pending-text dark:bg-amber-500/20 dark:text-hive-orange-light'
                          }`}>
                            {req.status === 'approved' ? (req.auto_approved ? '✓ Automatic' : '✓') : '⏳'}
                          </span>
                          {req.status === 'pending' && (
                            <button
                              onClick={() => handleCancel(req.id)}
                              className="opacity-0 group-hover:opacity-100 text-destructive hover:bg-destructive/10 p-1 rounded transition-opacity"
                              title="Cancel request"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* The cancel box — the whole booking or just some of its days (DESIGN-part-cancel.md). */}
      <CancelLeaveDialog requestId={cancelId} onClose={() => setCancelId(null)} onDone={fetchData} />

      {/* Request Leave Dialog */}
      <RequestLeaveDialog 
        open={requestDialogOpen} 
        onOpenChange={(open) => {
          setRequestDialogOpen(open);
          if (!open) {
            // Clear quick book values when dialog closes
            setQuickBookDate(null);
            setQuickBookLeaveTypeId(null);
          }
        }}
        onSuccess={fetchData}
        initialDate={quickBookDate}
        initialLeaveTypeId={quickBookLeaveTypeId}
      />
      
      {/* Block Leave Dialog */}
      {blackoutDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-card rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-400" />
              {editingBlackout ? 'Change no-leave dates' : 'No-leave dates'}
            </h3>
            {!editingBlackout && (
              <div className="inline-flex gap-1 p-1 rounded-full bg-hive-line-soft dark:bg-secondary mb-4" role="tablist">
                {[['add', 'Add new'], ['yours', `Yours (${mine(blackouts, meId).length})`]].map(([k, label]) => (
                  <button key={k} type="button" role="tab" aria-selected={blackoutTab === k}
                    onClick={() => setBlackoutTab(k)} data-testid={`no-leave-tab-${k}`}
                    className={`px-3.5 py-1.5 rounded-full text-[13px] font-bold transition-colors ${blackoutTab === k ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}

            {blackoutTab === 'yours' && !editingBlackout ? (
              <>
                <NoLeaveDatesYours blackouts={blackouts} meId={meId} onChange={openChangeBlackout} onRemoved={refreshBlackouts} />
                <div className="flex justify-end mt-6">
                  <Button variant="secondary" onClick={closeBlackoutBox}>Close</Button>
                </div>
              </>
            ) : (<>
            <div className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={blackoutForm.name}
                  onChange={(e) => setBlackoutForm({ ...blackoutForm, name: e.target.value })}
                  placeholder="e.g., Christmas Shutdown, Month-End Close"
                  className="input-hive mt-1"
                />
              </div>
              
              <div>
                <Label>Reason (optional)</Label>
                <Input
                  value={blackoutForm.reason}
                  onChange={(e) => setBlackoutForm({ ...blackoutForm, reason: e.target.value })}
                  placeholder="e.g., All staff required for stocktake"
                  className="input-hive mt-1"
                />
              </div>
              
              {/* ONE pop-up for both days (DateRangeField, Rob 3A 02/11/2026). The form keeps its
                  yyyy-mm-dd strings; the field works in days. */}
              <DateRangeField
                startLabel="Start date *"
                endLabel="End date *"
                start={blackoutForm.start_date ? parseISO(blackoutForm.start_date) : null}
                end={blackoutForm.end_date ? parseISO(blackoutForm.end_date) : null}
                onChange={({ start, end }) => setBlackoutForm({ ...blackoutForm, start_date: start ? format(start, 'yyyy-MM-dd') : '', end_date: end ? format(end, 'yyyy-MM-dd') : '' })}
              />
              
              {editingBlackout ? (
                <p className="text-sm"><span className="text-muted-foreground">For: </span><span className="font-semibold">
                  {editingBlackout.scope === 'company' ? 'Everyone' : editingBlackout.scope === 'department' ? (editingBlackout.department_name || 'A department') : (editingBlackout.user_name || 'One person')}
                </span></p>
              ) : (<>
              <div>
                <Label>Applies To</Label>
                <select
                  value={blackoutForm.scope}
                  onChange={(e) => setBlackoutForm({ ...blackoutForm, scope: e.target.value, user_ids: [] })}
                  className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">Choose option...</option>
                  {blackoutWholeCompany && (
                    <option value="company">Everyone (Company-wide)</option>
                  )}
                  <option value="department">Specific Department</option>
                  <option value="user">Specific Employee(s)</option>
                </select>
              </div>
              
              {blackoutForm.scope === 'department' && (
                <div>
                  <Label>Department *</Label>
                  <select
                    value={blackoutForm.department_id}
                    onChange={(e) => setBlackoutForm({ ...blackoutForm, department_id: e.target.value })}
                    className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select department...</option>
                    {/* Already only the departments this person may block (the server's list) */}
                    {allDepartments
                      .map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))
                    }
                  </select>
                </div>
              )}
              
              {blackoutForm.scope === 'user' && (
                <div>
                  <Label>Employee(s) * <span className="text-xs text-muted-foreground font-normal">(select one or more)</span></Label>
                  <div className="mt-2 max-h-48 overflow-y-auto border border-input rounded-md bg-background">
                    {/* Already only the people this person may block (the server's list) */}
                    {allEmployees
                      .map(emp => (
                        <label 
                          key={emp.id} 
                          className="flex items-center gap-3 px-3 py-2 hover:bg-secondary/50 cursor-pointer border-b border-border last:border-0"
                        >
                          <input
                            type="checkbox"
                            checked={blackoutForm.user_ids.includes(emp.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setBlackoutForm({ ...blackoutForm, user_ids: [...blackoutForm.user_ids, emp.id] });
                              } else {
                                setBlackoutForm({ ...blackoutForm, user_ids: blackoutForm.user_ids.filter(id => id !== emp.id) });
                              }
                            }}
                            className="w-4 h-4 rounded border-input accent-primary"
                          />
                          <span className="text-sm">{emp.first_name} {emp.last_name}</span>
                        </label>
                      ))
                    }
                  </div>
                  {blackoutForm.user_ids.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {blackoutForm.user_ids.length} employee(s) selected
                    </p>
                  )}
                </div>
              )}
              </>)}
              
              <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-secondary/50 transition-colors">
                <input
                  type="checkbox"
                  checked={blackoutForm.is_recurring}
                  onChange={(e) => setBlackoutForm({ ...blackoutForm, is_recurring: e.target.checked })}
                  className="w-5 h-5 rounded border-input accent-primary"
                />
                <div>
                  <p className="font-medium">Recurring annually</p>
                  <p className="text-xs text-muted-foreground">These dates will repeat every year</p>
                </div>
              </label>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button 
                variant="secondary" 
                className="flex-1"
                onClick={closeBlackoutBox}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white" 
                onClick={handleSaveBlackout}
                disabled={blackoutSaving}
              >
                {blackoutSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
                {editingBlackout ? 'Save changes' : 'Add no-leave dates'}
              </Button>
            </div>
            </>)}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyLeavePage;
